import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line max-len
import { type FinderVehicleManagementModuleSpecsFragment } from '../../../../api/fragments/FinderVehicleManagementModuleSpecs';
import {
    UpdateFinderVehicleManagementModuleDocument,
    type UpdateFinderVehicleManagementModuleMutation,
    type UpdateFinderVehicleManagementModuleMutationVariables,
} from '../../../../api/mutations/updateFinderVehicleManagementModule';
import { type FinderVehicleManagementModuleSetting } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import FinderVehicleManagementModuleDetailsForm from './FinderVehicleManagementModuleDetailsForm';

export type FormValues = FinderVehicleManagementModuleSetting;

export type MainDetailsProps = {
    module: FinderVehicleManagementModuleSpecsFragment;
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredBoolean('allowLTA'),
    validators.requiredString('secrets.apiKey'),
    validators.requiredString('secrets.secretKey'),
    validators.requiredString('secrets.loginEndpoint'),
    validators.requiredString('secrets.apiEndpoint'),
    validators.requiredString('secrets.finderUrl')
);

const FinderVehicleManagementModuleDetailsInner = ({ module }: MainDetailsProps) => {
    const { t } = useTranslation(['moduleDetails', 'finderVehicleManagementModuleDetails']);

    const validate = useValidator(validator);

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['allowLTA', 'secrets'], module.setting),
            displayName: module.displayName,
            autoplaySettingId: module.autoplaySettingId,
        }),
        [module]
    );

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<FinderVehicleManagementModuleSetting>(
        async values => {
            // submitting message
            message.loading({
                content: t('finderVehicleManagementModuleDetails:messages.updateMainDetailsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient
                .mutate<
                    UpdateFinderVehicleManagementModuleMutation,
                    UpdateFinderVehicleManagementModuleMutationVariables
                >({
                    mutation: UpdateFinderVehicleManagementModuleDocument,
                    variables: {
                        moduleId: module.id,
                        settings: values,
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('finderVehicleManagementModuleDetails:messages.updateMainDetailsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateMainDetails" name="updateMainDetails" onSubmitCapture={handleSubmit}>
                    <FinderVehicleManagementModuleDetailsForm module={module} />
                </Form>
            )}
        </Formik>
    );
};

export default FinderVehicleManagementModuleDetailsInner;
